/**
 * Decides what the Prospects page says about the state of the book, and how loudly.
 *
 * The page used to stack up to five alerts above the list, two of them permanent. This sorts the same
 * facts by what the advisor can do about them:
 *
 * - One banner at most, for something wrong that needs acting on: the CRM is disconnected, the last
 *   sync stopped early, or a field the whole list depends on is not mapped. The first that applies
 *   wins.
 * - Everything that lowers how far the list can be trusted goes in a "data quality" list the advisor
 *   can open, each with a way to fix it.
 * - Work in progress (a sync, a scoring pass) is a status beside the title, not an alert.
 * - A model that has not been approved is a "Preview" badge.
 *
 * Nothing is dropped. PRD §4.3 and §8.2 require that the list never looks more certain than it is,
 * so what was an alert is still on the page, smaller.
 */
import type { Prospecting } from '@client/api/types';

export type BookBannerKind = 'disconnected' | 'lastSyncIncomplete' | 'essentialFieldUnmapped';

export type BookActivity = 'syncing' | 'scoring';

export interface BookNotices {
  banner: BookBannerKind | null;
  /** The essential fields behind an `essentialFieldUnmapped` banner. */
  unmappedEssentials: Prospecting.ConfidenceReason[];
  /** What makes the list less reliable, most serious first. Empty when nothing does. */
  qualityReasons: Prospecting.ConfidenceReason[];
  activity: BookActivity | null;
  isPreview: boolean;
}

/** Without these, most of the list cannot be scored, or people who opted out stay on it. */
const ESSENTIAL: Prospecting.ConfidenceReason[] = ['birthDateNotMapped', 'doNotContactNotMapped'];

/** Reasons the advisor closes by mapping a field. */
export const FIXED_BY_MAPPING: ReadonlySet<Prospecting.ConfidenceReason> = new Set([
  'birthDateNotMapped',
  'doNotContactNotMapped',
  'firmAssetsOnly',
  'noAssets',
]);

export function bookNotices(book: Prospecting.BookStatus): BookNotices {
  const reasons = book.confidence.reasons;
  const unmappedEssentials = ESSENTIAL.filter(reason => reasons.includes(reason));
  // The model's approval is the Preview badge's to say, once.
  const quality = reasons.filter(reason => reason !== 'modelNotSignedOff');

  return {
    banner: !book.connected
      ? 'disconnected'
      : book.freshness.lastSyncComplete === false
        ? 'lastSyncIncomplete'
        : unmappedEssentials.length > 0
          ? 'essentialFieldUnmapped'
          : null,
    unmappedEssentials,
    qualityReasons: [...unmappedEssentials, ...quality.filter(reason => !ESSENTIAL.includes(reason))],
    activity: book.freshness.syncActive ? 'syncing' : book.scoring.pending ? 'scoring' : null,
    isPreview: !book.scoring.modelSignedOff,
  };
}
